import { RelationshipValue, CoverComposition, PolicyStatus } from '@general/js/enums';
import { InsurableTypeEnum, CoverCompositionCRM } from '@/enums';
import { getNonDependantList } from './EnumUtils';
import { sortByObjectProperty } from './SortUtils';

/**
 * Finds and returns accessLevel for given `memberNumber` from an array of different `memberships`
 *
 * @param {string} memberNumber this is also known as policy number
 * @param {Array} memberships this is array of object, each object containing member number, access level and covered as
 * @returns {string} If found, return access level data in all lower case, else return ''
 */
export const getAccessLevel = (memberNumber, memberships) => {
  if (memberships) {
    return memberships
      .find((obj) => {
        return obj.memberNumber === memberNumber;
      })
      ?.accessLevel?.toLowerCase();
  }

  return '';
};

/**
 * Finds and returns accessLevel for given `memberNumber` from an array of different `memberships`
 *
 * @param {string} memberNumber this is also known as policy number
 * @param {Array} productList this is array of object, each object containing member number and coveredOnMembership
 * @returns {string} If found, return access level data in all lower case, else return ''
 */
export const getCoveredOnMembership = (memberNumber, productList) => {
  if (productList) {
    return productList
      .find((obj) => {
        return obj.memberNumber === memberNumber;
      })
      ?.coveredOnMembership?.toLowerCase();
  }

  return '';
};

/**
 * Retrieves coveredAs information for given member number.
 *
 * @param {string} memberNumber this is also known as policy number
 * @param {string} memberships this is array of object, each object containing member number, access level and covered as
 * @returns {string} return covered-as data in all upper case
 */
export const getCoveredAs = (memberNumber, memberships) => {
  if (memberships) {
    return memberships
      .find((obj) => {
        return obj.memberNumber === memberNumber;
      })
      ?.coveredAs?.toUpperCase();
  }

  return '';
};

/**
 * Function that will sort a list of unsorted member list, with option that filter out member who's insurance type is either contributor or responsible.
 * @param {Array} unsortedMemberList The unsorted member list, it assumes that the array will contain objects that at a minimum will have {name: 'John Smith', relationship: 'Spouse'}.
 * @param {string} [composition=null] Optional param, conposition is a child only policy, it will not include the policy holder in the returned list
 * @param {Boolean} [constainsInsurableType=false] Optional param, where the member list items will contain insurable type that should be used to filter selectable members.
 * @returns {Array} The sorted member list according to policy holder, partner and alphabetical dependants.
 */
export const sortMembersByRelationship = (unsortedMemberList, composition = null, containsInsurableType = false) => {
  if (!Array.isArray(unsortedMemberList) || unsortedMemberList.length === 0) {
    return [];
  }

  const nonDependantList = getNonDependantList();

  /**
   * This is an internal function that will check whether the person is a contributor or responsible person so that it can be filtered.
   * @param {Object} person The person object that is being looked at for whether the insurable type is contributor or responsible person.
   * @returns {Boolean} True if the person is either a contributor or responsible person. False otherwise.
   */
  const isContributorOrResponsible = (person) => {
    return [InsurableTypeEnum.CONTRIBUTOR, InsurableTypeEnum.RESPONSIBLE].includes(person.insurableType);
  };

  const sortedMemberList = [];

  // Get policy holder
  let policyHolder = unsortedMemberList.find((obj) => {
    return obj.relationship.toUpperCase() === nonDependantList[0];
  });

  // Get partner
  let partner = unsortedMemberList.find((obj) => {
    return nonDependantList.slice(1).includes(obj.relationship.toUpperCase());
  });

  // Check to see if the policy holder or partner needs to be filtered out due to being a contributor or responsible person, and if so it should be set to be undefined
  // so that it will not appear in the list of people in drop downs.
  if (containsInsurableType) {
    if (policyHolder && isContributorOrResponsible(policyHolder)) {
      policyHolder = undefined;
    }

    if (partner && isContributorOrResponsible(partner)) {
      partner = undefined;
    }
  }

  // Get other members in policy
  const otherMembers = unsortedMemberList.filter((obj) => {
    return !nonDependantList.includes(obj.relationship.toUpperCase());
  });

  // Only include policy holder and partner in the list if composition is not "childrenOnly" and "Child/Children only"
  if (
    composition == null ||
    (composition.toLowerCase() !== CoverComposition.CHILDREN_ONLY.toLowerCase() && composition.toLowerCase() !== CoverCompositionCRM.CHILDREN_ONLY.toLowerCase())
  ) {
    // If there is policy holder and policy holder is not contributor and responsible person
    if (policyHolder) {
      sortedMemberList.push(policyHolder);
    }
    if (partner) {
      sortedMemberList.push(partner);
    }
  }

  if (otherMembers.length > 0) {
    const otherMembersNames = otherMembers
      .map((obj) => {
        return obj;
      })
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

    // Need to use push, since there may be cases where the member list is empty, and concat for some reason will cause errors to appear.
    sortedMemberList.push(...otherMembersNames);
  }

  return sortedMemberList;
};

/**
 * Sorts members list based on `relationshipKeyName` (default=`coveredOnMembership`) value in the order: [Policy holder, Non dependent, Dependents]. If there are multiple members of same type (dependent vs non-dependent, they will be sorted by the `sortOn` property in the `sortOrder` order
 * @param {Array} unsortedMembersArr Unsorted list of all members
 * @param {String} [relationshipKeyName=coveredOnMembership] Property name in each member object which contains the "relationship" value to sort on
 * @param {string} [subSortOn=firstName] If there are multiple members of same type (dependent vs non-dependent), property name for each member object to sort on
 * @param {string} [subSortOrder=ascending] Order to sort in, i.e `ascending` or `descending`
 * @param {string} [composition=null] Optional param, conposition is a child only policy, it will not include the policy holder in the returned list
 * @returns Sorted list of members(Policy holder, Daughter, Ex spouse, etc), or original unsorted list in case of errors.
 */
export const sortMembersByRelationshipDisplay = (
  unsortedMembersArr,
  relationshipKeyName = 'coveredOnMembership',
  subSortOn = 'firstName',
  subSortOrder = 'ascending',
  composition = null,
) => {
  try {
    const sortedMemberList = [];

    // create a list of non-dependent relationships and capitalize them.
    const nonDependantRelationships = [RelationshipValue.SPOUSE, RelationshipValue.DEFACTO, RelationshipValue.EX_SPOUSE].map((str) => {
      return str.toUpperCase();
    });
    // filter non dependant members except the policy holder
    const nonDependants = unsortedMembersArr.filter((obj) => {
      return nonDependantRelationships.includes(obj[relationshipKeyName].toUpperCase());
    });
    // filter dependant members
    const dependants = unsortedMembersArr.filter((obj) => {
      const relationship = obj[relationshipKeyName].toUpperCase();
      return !nonDependantRelationships.includes(relationship) && relationship !== RelationshipValue.POLICY_HOLDER.toUpperCase();
    });

    // find policy holder
    const policyHolder = unsortedMembersArr.find((obj) => {
      return obj[relationshipKeyName].toUpperCase() === RelationshipValue.POLICY_HOLDER.toUpperCase();
    });

    // Only include policy holder and partner in the list if composition is not "childrenOnly"
    if (composition == null || composition.toLowerCase() !== CoverComposition.CHILDREN_ONLY.toLowerCase()) {
      if (policyHolder) {
        sortedMemberList.push(policyHolder);
      }
      if (Array.isArray(nonDependants) && nonDependants.length > 0) {
        sortedMemberList.push(...sortByObjectProperty(nonDependants, subSortOn, null, subSortOrder));
      }
    }
    if (Array.isArray(dependants) && dependants.length > 0) {
      sortedMemberList.push(...sortByObjectProperty(dependants, subSortOn, null, subSortOrder));
    }

    return sortedMemberList;
  } catch (e) {
    logger.error('Error while sorting in sortMembersByRelationshipDisplay:', e);
    // if any errors, return original list
    return unsortedMembersArr;
  }
};

/**
 * This function searches for the policy holder's details from list of `members`, or `null` otherwise.
 * @param {Array} members List of all members
 * @param {Boolean} [findActivePH=false] If `true`, it will only return the details of the "active" policy holder (by looking at member's `latestMemberStatus`). `false` by default
 * @param {Boolean} [isChangeCover=false] If `true`, it will look for the policy holder based on the `relationship.name` property in each member object(used to look for PH in CoC/AddRemovePerson). `false` by default
 * @param {string} [relationshipKeyName=coveredOnMembership] Property name in each member object which contains the "relationship" value to filter on. This property accepts dot notation for nested values, eg. `relationship.name`. This property is only considered if `isChangeCover` is `false`
 * @returns Policy holder's object from the list or `null` if not found/any errors.
 */
export const findPolicyHolder = (members, findActivePH = false, isChangeCover = false, relationshipKeyName = 'coveredOnMembership') => {
  // make a copy of the keyName
  let keyName = relationshipKeyName;
  // In change cover, then the keyName should be `relationship.name`
  if (isChangeCover) {
    keyName = 'relationship.name';
  }

  try {
    // find policy holder
    const policyHolder = members.find((member) => {
      let relationship = member;
      const keys = keyName.split('.');
      // logic to get value from nested keys
      for (let i = 0; i < keys.length; i++) {
        relationship = relationship[keys[i]];
      }

      if (findActivePH) {
        return relationship.toUpperCase() === RelationshipValue.POLICY_HOLDER.toUpperCase() && member.latestMemberStatus?.toLowerCase() === PolicyStatus.ACTIVE.toLowerCase();
      }

      return relationship.toUpperCase() === RelationshipValue.POLICY_HOLDER.toUpperCase();
    });
    return policyHolder;
  } catch (e) {
    logger.error('Error in findPolicyHolder:', e);
  }
  return null;
};

/**
 * Retrieves partner details from a list of members from CRM or returns `undefined` if no partner information is found.
 * @param {Array} members - A list of member details obtained from `dhl_membershipmemberses`.
 * @param {Boolean} [findActivePartner=false] - If `true`, it will only return the details of the "active" partner (by looking at member's `dhl_latestmemberstatus`). `false` by default
 * @returns {Object|undefined} - The partner's object from the list or `undefined` if not found or in case of errors.
 */
export const getPartnerDetails = (members, findActivePartner = false, findNotCoveredPartner = false) => {
  // Note: Currently, it will only return the details of the first partner, and there's a slight chance that there might be more than one partner on a single policy.
  const partnerInfo = members?.find((member) => {
    if (member.dhl_relationship && member.dhl_relationship.dhl_name) {
      const isPartnerRelation = [RelationshipValue.SPOUSE.toLowerCase(), RelationshipValue.DEFACTO.toLowerCase(), RelationshipValue.EX_SPOUSE.toLowerCase()].includes(
        member.dhl_relationship.dhl_name.toLowerCase(),
      );

      if (isPartnerRelation && (findActivePartner || findNotCoveredPartner)) {
        const isActive = member.dhl_latestmemberstatus.toLowerCase() === PolicyStatus.ACTIVE.toLowerCase();
        const isNotCovered = member.dhl_latestmemberstatus.toLowerCase() === PolicyStatus.NOT_COVERED.toLowerCase();

        if (findActivePartner && findNotCoveredPartner) {
          return isActive || isNotCovered;
        }
        if (findActivePartner) {
          return isActive;
        }
        if (findNotCoveredPartner) {
          return isNotCovered;
        }
      }

      return isPartnerRelation;
    }
    return false;
  });

  return partnerInfo;
};

/**
 * Retrieves partner details from a list of members from the member portal or returns false if no partner information is found.
 * @param {Array} members - A list of member details obtained from `relationship` object within member object.
 * @param {Boolean} [findActivePartner=false] - If `true`, it will only return the details of the "active" partner (by looking at member's `latestMemberStatus`). `false` by default
 * @returns {Object|boolean} - The partner's object from the list or false if not found or in case of errors.
 */
export const getPartnerDetailsMP = (members, findActivePartner = false) => {
  // Note: Currently, it will only return the details of the first partner, and there's a slight chance that there might be more than one partner on a single policy.
  const partnerInfo = members?.find((member) => {
    if (member.relationship?.name) {
      const isPartnerRelation = [RelationshipValue.SPOUSE.toLowerCase(), RelationshipValue.DEFACTO.toLowerCase(), RelationshipValue.EX_SPOUSE.toLowerCase()].includes(
        member.relationship.name.toLowerCase(),
      );
      if (findActivePartner) {
        return isPartnerRelation && member.latestMemberStatus.toLowerCase() === PolicyStatus.ACTIVE.toLowerCase();
      }
      return isPartnerRelation;
    }
    return false;
  });

  return partnerInfo;
};
